import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useUpdateNomination } from '@/api/queries';
import type { EventCard } from '@/api/types';
import { Button } from '@/components/kit/Button';
import { ActionBar } from '@/components/kit/ActionBar';
import { MetricRow } from '@/components/kit/MetricRow';
import { SectionHeader } from '@/components/kit/ObjectTabs';
import { Alert, EmptyState } from '@/components/kit/Note';
import { Panel, Signals } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { CardLabel, CellInput, MobileSub, Na, Table } from '@/components/kit/Table';
import { ageRange, fraction, mmss, nominations as nomPlural, num } from '@/lib/format';
import type { TabKey } from '../EventPage';
import { NominationModal } from './NominationModal';

/**
 * Вкладка «Номинации».
 *
 * В таблице правится только лимит — его двигают по ходу регистрации,
 * не открывая карточку. Остальные десять параметров живут в карточке:
 * таблица настроек, которую правят целиком, превращается в форму,
 * притворяющуюся списком.
 */
export function NominationsTab({
  event,
  onGoTab,
}: {
  event: EventCard;
  onGoTab: (tab: TabKey) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const update = useUpdateNomination(event.id);
  const [adding, setAdding] = useState(false);

  const { nominations, packages, blocks, computed } = event;
  const packageName = (id: string) => packages.find((p) => p.id === id)?.name ?? '—';
  const blockName = (id: string | null) => blocks.find((b) => b.id === id)?.name ?? null;

  const badDefault = nominations.filter((n) => computed.nominationsWithBadDefault.includes(n.id));
  const outOfProgram = nominations.filter((n) => computed.nominationsOutOfProgram.includes(n.id));
  const competitive = nominations.filter((n) => n.isCompetitive).length;

  const setLimit = (id: string, teamLimit: number) =>
    update.mutate(
      { id, teamLimit },
      { onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось') },
    );

  const addButton = (
    <Button small tone="quiet" onClick={() => setAdding(true)}>
      ＋ Номинация
    </Button>
  );

  const modal = adding ? (
    <NominationModal event={event} onClose={() => setAdding(false)} />
  ) : null;

  const header = <SectionHeader actions={addButton} />;
  const abar = <ActionBar>{addButton}</ActionBar>;

  if (!nominations.length) {
    return (
      <>
        <Panel>
          {header}
          <EmptyState>
            Номинаций пока нет. Без них не получится ни собрать программу,
            <br />
            ни выпустить пакет в продажу
          </EmptyState>
        </Panel>
        {abar}
        {modal}
      </>
    );
  }

  return (
    <>
      <Signals>
        {badDefault.length ? (
          <Alert
            action={
              <Button small tone="quiet" onClick={() => onGoTab('packages')}>
                Открыть пакеты
              </Button>
            }
          >
            <b>У {nomPlural(badDefault.length)} не один пакет по умолчанию.</b>
            <span>{badDefault.map((n) => n.name).join(', ')} — клиент не поймёт, что покупать</span>
          </Alert>
        ) : null}

        {outOfProgram.length ? (
          <Alert
            action={
              <Button small tone="quiet" onClick={() => onGoTab('program')}>
                Открыть программу
              </Button>
            }
          >
            <b>{nomPlural(outOfProgram.length)} вне программы.</b>
            <span>
              {outOfProgram.map((n) => n.name).join(', ')} — не попадут в порядок выступлений
            </span>
          </Alert>
        ) : null}
      </Signals>

      <Panel>
        {header}

        {/* Срез раздела (кит 2.11): размер цифры не зависит от их числа */}
        <MetricRow
          variant="slice"
          items={[
            {
              label: 'Номинаций',
              value: num(nominations.length),
              sub: (
                <>
                  конкурсных <span className="num">{competitive}</span>, показательных{' '}
                  <span className="num">{nominations.length - competitive}</span>
                </>
              ),
            },
            {
              label: 'Лимит команд',
              value: num(computed.teamLimitTotal),
              sub: 'сумма лимитов по номинациям',
            },
            {
              label: 'В программе',
              value: fraction(nominations.length - outOfProgram.length, nominations.length),
              sub: outOfProgram.length
                ? `${nomPlural(outOfProgram.length)} ещё не в блоках`
                : 'все разложены по блокам',
            },
          ]}
        />

        <Table
          valueWidth={130}
          head={
            <tr>
              <th className="srt">
                Номинация <span className="ar">▲</span>
              </th>
              <th className="r">Возраст</th>
              <th className="r">Хроно</th>
              <th className="r">Ср. время</th>
              <th>Пакет по умолчанию</th>
              <th className="r">Ещё доступны</th>
              <th>Блок</th>
              <th className="r">Лимит команд</th>
              <th />
            </tr>
          }
        >
          {nominations.map((n) => {
            const block = blockName(n.blockId);
            const others = Math.max(n.packageIds.length - 1, 0);
            const badHere = computed.nominationsWithBadDefault.includes(n.id);

            return (
              <tr key={n.id}>
                <td data-col="primary">
                  <span
                    className="nm"
                    onClick={() => navigate(`/events/${event.id}/nominations/${n.id}`)}
                  >
                    {n.name}
                  </span>
                  <MobileSub>
                    {ageRange(n.ageFrom, n.ageTo)} · хроно {mmss(n.durationLimitSec)} ·{' '}
                    {block ?? 'вне программы'}
                  </MobileSub>
                </td>
                <td className="r num muted" data-col="hide">
                  {ageRange(n.ageFrom, n.ageTo)}
                </td>
                <td className="r num muted" data-col="hide">
                  {mmss(n.durationLimitSec)}
                </td>
                <td className="r num muted" data-col="hide">
                  {mmss(n.avgDurationSec)}
                </td>
                <td
                  data-col="secondary"
                  className={badHere ? 'ell wtx' : ''}
                >
                  <CardLabel>по умолчанию</CardLabel>
                  {n.defaultPackageId
                    ? packageName(n.defaultPackageId)
                    : badHere
                      ? 'не задан'
                      : <Na />}
                </td>
                <td className="r num muted" data-col="hide">
                  {others || <span className="na">—</span>}
                </td>
                <td className="muted" data-col="hide">
                  {block ?? <span className="wtx">вне программы</span>}
                </td>
                <td className="r" data-col="sec2">
                  <CardLabel>лимит команд</CardLabel>
                  <CellInput value={n.teamLimit} onCommit={(v) => setLimit(n.id, v)} />
                </td>
                <td className="r" data-col="act" />
              </tr>
            );
          })}

          <tr className="tot">
            <td colSpan={7}>Итого · {nomPlural(nominations.length)}</td>
            <td className="r num" data-col="sec2">
              {num(computed.teamLimitTotal)}
            </td>
            <td className="r" data-col="act" />
          </tr>
        </Table>
      </Panel>

      {abar}
      {modal}
    </>
  );
}
